import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Key, Plus, RefreshCw, X } from 'lucide-react';
import type { ApiKey, IssuedApiKey, OrgMemberSummary } from '../../generated/api/types.gen.js';
import {
  listApiKeyGrantablePermissionsOptions,
  listApiKeysOptions,
  listApiKeysQueryKey,
  listOrgMembersOptions,
  revokeApiKeyMutation,
  rotateApiKeyMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterPills,
  SearchBar,
  Toolbar,
  useToast,
} from '../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { useAuthStore } from '../../store/index.js';
import { IssueApiKeyModal } from './IssueApiKeyModal.js';
import { IssuedKeyCallout } from './IssuedKeyCallout.js';
import { formatDate } from './user-labels.js';

const SCOPE_LABEL: Record<ApiKey['scope'], string> = {
  worker_sdk: 'Runtime',
  service_account: 'Deployment',
  user: 'Personal',
  host_reporter: 'Host metrics',
};

const SCOPE_PILLS = [
  { id: 'all', label: 'All' },
  { id: 'worker_sdk', label: 'Runtime' },
  { id: 'service_account', label: 'Deployment' },
  { id: 'user', label: 'Personal' },
  { id: 'host_reporter', label: 'Host metrics' },
];

function keyStatus(key: ApiKey): { label: string; variant: BadgeVariant; dead: boolean } {
  if (key.revokedAt) {
    if (new Date(key.revokedAt) > new Date()) return { label: 'Rotating out', variant: 'warning', dead: false };
    return { label: 'Revoked', variant: 'danger', dead: true };
  }
  if (key.expiresAt && new Date(key.expiresAt) < new Date()) return { label: 'Expired', variant: 'danger', dead: true };
  return { label: 'Active', variant: 'success', dead: false };
}

function memberLabel(member: OrgMemberSummary | undefined): string {
  if (!member) return 'Unknown person';
  return member.fullName ?? member.username ?? member.email;
}

export const ApiKeysPage: React.FC = () => {
  const help = PAGE_HELP['api-keys'];
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [showIssue, setShowIssue] = useState(false);
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [search, setSearch] = useState('');
  const [scopeId, setScopeId] = useState('all');

  const orgId = useAuthStore((s) => s.user?.organizationId ?? null);
  const currentUserId = useAuthStore((s) => (s.user ? Number(s.user.id) : null));

  const keysQuery = useQuery(listApiKeysOptions());
  const membersQuery = useQuery({ ...listOrgMembersOptions({ path: { id: orgId ?? 0 } }), enabled: orgId !== null });
  const grantableQuery = useQuery(listApiKeyGrantablePermissionsOptions());

  const refresh = () => queryClient.invalidateQueries({ queryKey: listApiKeysQueryKey() });

  const revoke = useMutation({
    ...revokeApiKeyMutation(),
    onSuccess: async () => {
      setRevoking(null);
      notify('Key revoked');
      await refresh();
    },
    onError: () => notify('Could not revoke the key', 'danger'),
  });
  const rotate = useMutation({
    ...rotateApiKeyMutation(),
    onSuccess: async (successor) => {
      setIssued(successor);
      await refresh();
    },
    onError: () => notify('Could not rotate the key', 'danger'),
  });

  const members = membersQuery.data?.members ?? [];
  const membersById = new Map(members.map((member) => [member.id, member]));
  const needle = search.trim().toLowerCase();
  const keys = (keysQuery.data ?? []).filter(
    (key) =>
      (scopeId === 'all' || key.scope === scopeId) &&
      (!needle || key.name.toLowerCase().includes(needle) || key.keyPrefix.toLowerCase().includes(needle)),
  );

  const issueButton = (
    <Can policies={['CanManageWorkerSdkKeys', 'CanUseAgentMcp']} mode="any">
      <Button onClick={() => setShowIssue(true)}>
        <Plus size={14} aria-hidden="true" />
        Issue key
      </Button>
    </Can>
  );

  const columns: DataTableColumn<ApiKey>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortValue: (key) => key.name.toLowerCase(),
      render: (key) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-heading">{key.name}</span>
          <span className="block font-mono text-xs text-muted">{key.keyPrefix}…</span>
        </div>
      ),
    },
    { key: 'scope', header: 'Scope', render: (key) => <Badge variant="neutral">{SCOPE_LABEL[key.scope]}</Badge> },
    {
      key: 'actsAs',
      header: 'Acts as',
      render: (key) =>
        key.userId === null ? (
          <span className="text-sm text-faint">Not a person</span>
        ) : (
          <div>
            <span className="text-sm text-body">{memberLabel(membersById.get(key.userId))}</span>
            {key.metadata?.grants && key.metadata.grants.length > 0 && (
              <span className="block text-xs text-muted">Narrowed to {key.metadata.grants.length} permissions</span>
            )}
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (key) => {
        const status = keyStatus(key);
        return (
          <Badge variant={status.variant} dot>
            {status.label}
          </Badge>
        );
      },
    },
    {
      key: 'lastUsedAt',
      header: 'Last used',
      sortable: true,
      sortValue: (key) => key.lastUsedAt,
      render: (key) => <span className="text-sm text-muted">{formatDate(key.lastUsedAt, 'Never')}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      sortValue: (key) => key.createdAt,
      render: (key) => <span className="text-sm text-muted">{formatDate(key.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '210px',
      render: (key) => {
        const { dead } = keyStatus(key);
        return (
          <Can policy="CanManageWorkerSdkKeys">
            <div className="flex items-center justify-end gap-1.5">
              <Button
                size="sm"
                variant="secondary"
                disabled={dead}
                loading={rotate.isPending && rotate.variables?.path.id === key.id}
                onClick={() => rotate.mutate({ path: { id: key.id } })}
                title="Issue a successor key. The old one keeps working for 24 hours."
              >
                <RefreshCw size={14} aria-hidden="true" />
                Rotate
              </Button>
              <Button size="sm" variant="ghost" disabled={dead} onClick={() => setRevoking(key)}>
                <X size={14} aria-hidden="true" />
                Revoke
              </Button>
            </div>
          </Can>
        );
      },
    },
  ];

  return (
    <ListPage
      title="API keys"
      subtitle="Runtime, deployment and personal keys for your organization."
      helpContent={help}
      actions={issueButton}
      alert={
        <>
          {issued && <IssuedKeyCallout issued={issued} onDismiss={() => setIssued(null)} />}
          {keysQuery.isError && (
            <Callout
              tone="danger"
              icon={AlertTriangle}
              title="Could not load API keys"
              actions={
                <Button size="sm" variant="secondary" onClick={() => keysQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              Check your connection and try again.
            </Callout>
          )}
          {(membersQuery.isError || grantableQuery.isError) && (
            <Callout tone="warning" icon={AlertTriangle} title="Personal keys cannot be issued right now">
              The list of people or permissions did not load. Reload the page to try again.
            </Callout>
          )}
        </>
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search by name or prefix…" />}
          pills={<FilterPills aria-label="Scope" options={SCOPE_PILLS} value={scopeId} onChange={setScopeId} />}
        />
      }
    >
      {!keysQuery.isError && (
        <DataTable
          columns={columns}
          rows={keys}
          rowKey={(key) => key.id}
          loading={keysQuery.isPending}
          aria-label="API keys"
          empty={
            <EmptyState
              icon={Key}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
              action={issueButton}
            />
          }
        />
      )}

      <IssueApiKeyModal
        open={showIssue}
        onClose={() => setShowIssue(false)}
        onIssued={(key) => {
          setIssued(key);
          void refresh();
        }}
        members={members}
        grantable={grantableQuery.data?.data ?? []}
        currentUserId={currentUserId}
      />
      <ConfirmDialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate({ path: { id: revoking.id } })}
        title="Revoke key"
        message={`Revoke "${revoking?.name ?? ''}"? It stops working immediately, and any process still using it starts failing.`}
        confirmLabel="Revoke key"
        loading={revoke.isPending}
      />
    </ListPage>
  );
};
